/* eslint-disable */
import React, {
  FocusEvent,
  forwardRef,
  memo,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react'
import Input, { InputProps } from 'core/components/inputs/Input'
import Combobox from './Combobox'
import Tag from 'core/components/misc/Tag'

import Menu, {
  MenuProps,
  StandardOptionData
} from 'core/components/dialogs/Menu'
import { ID } from 'types/entity'
import Popover from 'core/components/dialogs/Popover'
import { buildOptionsList, buildOptionsMap } from './helpers'
import cn from 'utils/cn'
import Icon from '../icons/Icon'
import { IconType } from '../icons/types'

const iconTypeList: string[] = Object.values(IconType)

export interface SelectProps<T extends { token_id: ID }> extends Omit<
  InputProps,
  'onChange' | 'type' | 'value' | 'children'
> {
  /**
   * Array of select options to display.
   */
  options: MenuProps<T>['options']
  /**
   * Hide selected option from menu
   */
  hideSelectedOption?: boolean
  /**
   * Callback fired when the selected value changes.
   */
  onChange?: MenuProps<T>['onChange']
  /**
   * The currently selected value(s).
   */
  value?: MenuProps<T>['value']
  /**
   * Custom function to display option values.
   */
  getDisplayValue?: (
    option?:
      | string
      | number
      | StandardOptionData<T>
      | ReactElement<{ value: T['token_id']; onClick: VoidFunction }>
  ) => ReactNode
  /**
   * If true, matches the dropdown width to the reference element.
   */
  matchReferenceWidth?: boolean
  /**
   * If true, uses tag-style options for selection.
   */
  useTagOptions?: boolean
  /**
   * If true, allows clearing the selected value(s).
   */
  clearable?: boolean
  /**
   * If true, closes the dropdown after selecting an option.
   */
  autoCloseOnSelect?: boolean
  /**
   * Icon or element to display when the dropdown is expanded.
   */
  dropdownExpandedIcon?: `${IconType}` | ReactElement
  /**
   * Visual style variant for the dropdown menu.
   */
  menuVariant?: MenuProps<T>['variant']
  /**
   * Additional class names for the select wrapper element.
   */
  wrapperClassName?: string
  /**
   * Additional class names for the dropdown menu.
   */
  dropdownClassName?: string
}

/**
 * Select component supporting single/multi-select, custom display, tag options, and dropdown icons.
 * Integrates with forms and allows custom rendering and styling.
 */
const Select = memo(
  forwardRef(
    <T extends { token_id: ID }>(
      {
        name = 'select-input',
        options = [],
        value = '',
        onChange,
        onFocus,
        suffix = 'chevron-down',
        getDisplayValue = (v) => {
          if (!v) return null
          if (typeof v === 'string' || typeof v === 'number') return v
          if ('label' in v) return v.label
          return v.props.value
        },
        disabled,
        matchReferenceWidth = true,
        useTagOptions = false,
        readOnly = false,
        dropdownExpandedIcon = 'chevron-up',
        clearable = false,
        autoCloseOnSelect = true,
        menuVariant = 'default',
        wrapperClassName = '',
        dropdownClassName = '',
        hideSelectedOption = false,
        status = 'idle',
        ...rest
      }: SelectProps<T>,
      ref: Ref<HTMLDivElement>
    ) => {
      const elementRef = useRef<HTMLDivElement>(null)
      const menuRef = useRef<HTMLDivElement>(null)
      const [menuExpanded, setMenuExpanded] = useState(false)
      const [highlightedIndex, setHighlightedIndex] = useState(-1)
      const allowMultipleSelection = Array.isArray(value)
      const customId = useId()
      const hasValue = allowMultipleSelection ? value.length > 0 : !!value

      const hashMap = useMemo(() => {
        return buildOptionsMap(options)
      }, [options])

      const selectedOptions = useMemo(() => {
        if (allowMultipleSelection) return value.map((v) => hashMap.get(v) ?? v)
        return [hashMap.get(value) ?? value]
      }, [allowMultipleSelection, hashMap, value])

      const suffixEl = useMemo(() => {
        if (readOnly) return undefined

        if (status === 'loading' || status === 'pending') {
          return <Icon name="loading" size={14} className="mb-[3px] ml-2" />
        }

        const isCustomSuffix =
          suffix !== undefined && !iconTypeList.includes(suffix as string)
        const icon = isCustomSuffix
          ? suffix
          : menuExpanded
            ? dropdownExpandedIcon
            : hasValue && clearable
              ? 'close'
              : suffix

        const showCloseIcon =
          hasValue && !menuExpanded && clearable && !readOnly

        const onClick: MouseEventHandler<SVGSVGElement> = (e) => {
          e.stopPropagation()

          setMenuExpanded(hasValue && clearable ? false : !menuExpanded)
          if (hasValue && clearable && icon === 'close')
            onChange?.(
              allowMultipleSelection
                ? ([] as Array<T['token_id'] | string | number>)
                : ''
            )
        }

        if (typeof icon === 'string' && iconTypeList.includes(icon)) {
          return (
            <Icon
              name={icon as IconType}
              width={9}
              height={9}
              className={cn(
                'text-xs p-px h-fit cursor-pointer',
                showCloseIcon && 'rounded-full bg-dark-3 hover:bg-dark-4'
              )}
              color={showCloseIcon ? 'text-white' : 'text-dark-3'}
              onClick={onClick}
            />
          )
        }

        return (
          <span className="cursor-pointer text-xs text-dark-3 hover:text-dark-4">
            {icon}
          </span>
        )
      }, [
        readOnly,
        status,
        allowMultipleSelection,
        clearable,
        dropdownExpandedIcon,
        hasValue,
        menuExpanded,
        onChange,
        suffix
      ])

      const prefixEl = useMemo(() => {
        if (!rest.prefix) return null
        if (rest.prefix && (rest.prefix as string) in IconType) {
          return (
            <Icon
              name={rest.prefix as IconType}
              width={9}
              height={9}
              className={cn('text-xs p-px h-fit cursor-pointer')}
              color="text-dark-3"
            />
          )
        }

        return (
          <span className="cursor-pointer text-xs text-dark-3 mr-2">
            {rest.prefix}
          </span>
        )
      }, [rest.prefix])

      const onMenuItemOnChangeCB = useCallback(
        (v: typeof value) => {
          onChange?.(v)
          if (autoCloseOnSelect) {
            setMenuExpanded(Array.isArray(v) && v.length > 0)
          }
        },
        [onChange, autoCloseOnSelect]
      )
      const onFocusCB = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
          onFocus?.(e)
          // setMenuExpanded(!readOnly)
        },
        [onFocus]
      )

      useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
          if (autoCloseOnSelect === false) {
            if (
              event?.target &&
              // @ts-ignore
              !menuRef.current?.contains(event.target) &&
              // @ts-ignore
              !elementRef.current?.contains(event.target)
            ) {
              setMenuExpanded(false)
            }
            return
          }
          if (
            // @ts-ignore
            event?.target?.id?.includes('menu_scrollable_list') &&
            menuRef.current?.contains(event.target as Node)
          ) {
            return setMenuExpanded(true)
          }
          if (
            elementRef.current &&
            !elementRef.current.contains(event.target as Node) &&
            // @ts-ignore
            event?.target?.nodeName !== 'INPUT'
          ) {
            setMenuExpanded(false)
          }
        }

        const handleEscapeKey = (event: KeyboardEvent) => {
          if (event.key === 'Escape') {
            setMenuExpanded(false)
          }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscapeKey)

        return () => {
          document.removeEventListener('mousedown', handleClickOutside)
          document.removeEventListener('keydown', handleEscapeKey)
        }
      }, [autoCloseOnSelect, setMenuExpanded])

      const optionEls = useMemo(() => {
        if (useTagOptions) {
          return options.map((opt) => {
            if (typeof opt === 'string' || typeof opt === 'number')
              return (
                <Tag key={opt} value={opt}>
                  {opt}
                </Tag>
              )
            if ('token_id' in opt)
              return (
                <Tag
                  key={opt.token_id}
                  value={opt.token_id}
                  prefix={opt.prefix}
                  suffix={opt.suffix}
                >
                  {getDisplayValue(opt) ?? opt.token_id}
                </Tag>
              )
            if ('props' in opt) return opt
            throw new Error(
              'Select cannot accept react element options or 2D options when useTagOptions=true'
            )
          })
        }
        return hideSelectedOption
          ? options.filter((option) =>
              typeof option === 'object'
                ? (option as any)['token_id'] !== value
                : option !== value
            )
          : options
      }, [getDisplayValue, options, useTagOptions, value, hideSelectedOption])

      const displayValue = useMemo(
        () => getDisplayValue?.(selectedOptions[0]) ?? '',
        [getDisplayValue, selectedOptions]
      )

      // Flat list of selectable options used for keyboard navigation. The index
      // of each entry matches the `highlightedIndex` rendered by Menu (group
      // labels are skipped, exactly like Menu does internally). `domIndex` maps
      // back to the rendered `item-${i}` element so we can scroll it into view.
      const navigableOptions = useMemo(() => {
        return buildOptionsList(optionEls).reduce<
          Array<{
            token_id: ID | string | number
            disabled?: boolean
            domIndex: number
          }>
        >((acc, opt, domIndex) => {
          if (!('groupLabel' in opt)) {
            acc.push({
              token_id: opt.token_id,
              disabled: opt.disabled,
              domIndex
            })
          }
          return acc
        }, [])
      }, [optionEls])

      // Applies a selection coming from the keyboard, mirroring Menu's toggle
      // logic for multi-select so Enter and click stay consistent.
      const selectValue = useCallback(
        (tokenId: ID | string | number) => {
          if (!allowMultipleSelection) return onMenuItemOnChangeCB(tokenId)
          const next = value.includes(tokenId)
            ? value.filter((v) => v !== tokenId)
            : [...value, tokenId]
          onMenuItemOnChangeCB(next)
        },
        [allowMultipleSelection, onMenuItemOnChangeCB, value]
      )

      const moveHighlight = useCallback(
        (direction: 1 | -1) => {
          setHighlightedIndex((prev) => {
            const total = navigableOptions.length
            if (total === 0) return -1
            let next = prev
            for (let i = 0; i < total; i++) {
              next = (next + direction + total) % total
              if (!navigableOptions[next]?.disabled) return next
            }
            return prev
          })
        },
        [navigableOptions]
      )

      const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
          if (disabled || readOnly) return

          const isArrowDown = event.key === 'ArrowDown'
          const isArrowUp = event.key === 'ArrowUp'
          const isSelectKey = event.key === 'Enter' || event.key === ' '

          if (!isArrowDown && !isArrowUp && !isSelectKey) return

          event.preventDefault()

          // Any handled key opens the menu while it is closed.
          if (!menuExpanded) return setMenuExpanded(true)

          if (isArrowDown) return moveHighlight(1)
          if (isArrowUp) return moveHighlight(-1)

          const option = navigableOptions[highlightedIndex]
          if (option && !option.disabled) selectValue(option.token_id)
        },
        [
          disabled,
          highlightedIndex,
          menuExpanded,
          moveHighlight,
          navigableOptions,
          readOnly,
          selectValue
        ]
      )

      // Reset the highlight when closing, and default to the selected option (or
      // the first one) when opening, so arrow navigation starts somewhere sane.
      useEffect(() => {
        if (!menuExpanded) return setHighlightedIndex(-1)
        const selectedIndex = navigableOptions.findIndex((opt) =>
          allowMultipleSelection
            ? value.includes(opt.token_id)
            : opt.token_id === value
        )
        setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0)
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [menuExpanded])

      // Keep the highlighted option visible while navigating long lists.
      useEffect(() => {
        if (!menuExpanded || highlightedIndex < 0) return
        const option = navigableOptions[highlightedIndex]
        if (!option) return
        menuRef.current
          ?.querySelector(`#item-${option.domIndex}`)
          ?.scrollIntoView({ block: 'nearest' })
      }, [highlightedIndex, menuExpanded, navigableOptions])

      return (
        <div ref={ref} key={`select_${customId}`}>
          <div
            ref={elementRef}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleKeyDown}
            className={cn('inline-flex w-full', wrapperClassName)}
          >
            <Popover
              show={menuExpanded}
              position="bottom-left"
              useArrow={false}
              matchReferenceWidth={matchReferenceWidth}
              content={
                <Menu
                  ref={menuRef}
                  className={cn(['max-h-72 min-w-fit', dropdownClassName])}
                  options={optionEls}
                  value={value}
                  onChange={onMenuItemOnChangeCB}
                  variant={menuVariant}
                  highlightedIndex={highlightedIndex}
                />
              }
            >
              {allowMultipleSelection ? (
                <Combobox
                  onFocus={onFocusCB}
                  name={name}
                  values={selectedOptions}
                  suffix={suffixEl}
                  onChange={onMenuItemOnChangeCB}
                  //eslint-disable-next-line
                  //@ts-ignore intentional type ignoring
                  renderTag={getDisplayValue}
                  readOnly={readOnly}
                  {...rest}
                  onClick={(e: any) => {
                    e.stopPropagation()
                    rest?.onClick?.(e)
                    setMenuExpanded(!menuExpanded)
                  }}
                />
              ) : ['string', 'number'].includes(typeof displayValue) ? (
                <Input
                  name={name}
                  suffix={suffixEl}
                  disabled={disabled || readOnly}
                  value={displayValue.toString()}
                  onFocus={onFocusCB}
                  type="select"
                  {...rest}
                  status={status}
                  onClick={(e) => {
                    e.stopPropagation()
                    rest?.onClick?.(e)
                    setMenuExpanded(!menuExpanded)
                  }}
                  readOnly
                />
              ) : (
                // @TODO: extract this prefix-suffix-slot pattern to a base component and share this with Input, Button
                <div
                  className={cn(
                    'px-4 py-2 min-h-[35px] overflow-hidden bg-white text-default rounded-md outline-1 focus-within:outline-primary hover:outline-primary outline-dark-2 flex items-center justify-between gap-2 cursor-pointer',
                    disabled &&
                      'bg-dark-1 text-dark-7 cursor-not-allowed pointer-events-none',
                    rest.className,
                    rest.variant === 'invisible' &&
                      'outline-0! hover:outline-1 hover:outline-primary! bg-transparent'
                  )}
                  tabIndex={disabled ? -1 : 0}
                  onClick={() => setMenuExpanded(true)}
                >
                  <span className="w-full">
                    {prefixEl}
                    {displayValue}
                  </span>
                  {suffixEl}
                </div>
              )}
            </Popover>
          </div>
        </div>
      )
    }
  )
)

export default Select
